import {
  centsToDollars,
  totalSellerPriceCentsForEnrollments,
} from "@outschool/business-rules";
import {
  CreateEnrollmentMeetingPurchaseMutation,
  EnrollmentMeetingPurchaseQueryQuery,
  EnrollmentMeetingPurchaseQueryQueryVariables,
  SettleOrderEnrollmentMeetingPurchaseMutation,
  SettleOrderMutation,
} from "@outschool/gql-frontend-generated";
import { useCurrencyLocalization } from "@outschool/localization";
import { enrolledPath } from "@outschool/routes";
import { dayjs } from "@outschool/time";
import {
  IntegrationCategory,
  useOrderCompleted,
  useTrackEvent,
} from "@outschool/ui-analytics";
import { useQueryWithPreviousData } from "@outschool/ui-apollo";
import { currentUserQuery, useSession } from "@outschool/ui-auth";
import { LoadingModal, Modal } from "@outschool/ui-legacy-component-library";
import { useBooleanState } from "@outschool/ui-utils";
import React, { useState } from "react";
import { useHistory, useLocation } from "react-router";

import * as Routes from "../../shared/Routes";
import * as User from "../../shared/User";
import EnrollmentMeetingPurchaseWithStripe from "../components/enrolling/EnrollmentMeetingPurchaseWithStripe";
import { EsaMembershipSavePaymentMethod } from "../components/esaMemberships/EsaMembershipSavePaymentMethod";
import { usePaymentMethods } from "../hooks/CurrentUser";
import useSectionMeetings from "../hooks/useSectionMeetings";
import { isOrganizationAdminWhoCanEnroll } from "../lib/Users";
import { useTimeZone } from "../providers/TimeZoneProvider";
import { EnrollmentMeetingPurchaseQuery } from "../queries/EnrollmentMeetingPurchaseQuery";
import {
  useTrackNewBuyerAcquiredMutation,
  useTrackOrderCompletedMutation,
} from "../queries/TrackingMutation";
import { useAppState } from "../stores/AppStateProvider";

export enum PrepayType {
  ClassWallet = "ClassWallet",
  CharterAdmin = "CharterAdmin",
  CharterPO = "CharterPO",
}

export default function EnrollmentMeetingPurchasePage({
  activityUid,
  urlSectionUid,
  onClose = () => {},
}: {
  activityUid: string;
  onClose?: () => void;
  urlSectionUid?: string;
}) {
  const location = useLocation();
  const history = useHistory();
  const appState = useAppState();
  const trackeServerSideOrderCompleted = useTrackOrderCompletedMutation();
  const trackOrderCompleted = useOrderCompleted();
  const trackAdEvent = useTrackEvent(IntegrationCategory.Advertising);
  const { currencyCode } = useCurrencyLocalization();
  const trackNewBuyerAcquiredCapi = useTrackNewBuyerAcquiredMutation();
  const { isEsaSession } = useSession();

  const {
    startTime,
    enrollmentUid,
    orgTeacherUid,
    isPrepayMeetings,
    recurringScheduleUid,
    meetingRequestUids: meetingRequestUidsRaw,
  } = location.query;

  const [sectionUid, setSectionUid] = useState<string | null>(null);
  const [paymentComplete, setPaymentComplete] = useBooleanState(false);
  const [isRecurringEnrollment, setIsRecurringEnrollment] = useState(false);
  const [esaShowSavePayment, setEsaShowSavePayment] = React.useState(false);
  const [esaSavePaymentComplete, setEsaSavePaymentComplete] =
    React.useState(false);
  const { paymentMethods } = usePaymentMethods();
  const [defaultPaymentMethod] = paymentMethods;
  const meetingRequestUids = meetingRequestUidsRaw
    ? meetingRequestUidsRaw.split(",")
    : undefined;

  const [
    showMultipleMeetingsModal,
    setShowMultipleMeetingsModal,
    clearShowMultipleMeetingsModal,
  ] = useBooleanState(false);

  const { data, error, loading, refetch } = useQueryWithPreviousData<
    EnrollmentMeetingPurchaseQueryQuery,
    EnrollmentMeetingPurchaseQueryQueryVariables
  >(EnrollmentMeetingPurchaseQuery, {
    fetchPolicy: "network-only",
    variables: {
      activityUid,
      enrollmentUid,
      startTime,
      meetingRequestUids,
      orgTeacherUid,
    },
  });

  const meetingStart = dayjs(startTime);
  const meetingStartTime = meetingStart.toDate();

  const { timeZone } = useTimeZone();

  const startDate = dayjs.tz(startTime, timeZone);
  const startOfStartDateWeek = dayjs(startDate).startOf("week").toDate();
  const endOfStartDateWeek = dayjs(startDate).endOf("week").toDate();

  const { meetings } = useSectionMeetings(
    sectionUid ?? "",
    startOfStartDateWeek,
    endOfStartDateWeek,
  );

  const meetingsWeekOfStartDate = React.useMemo(
    () =>
      meetings?.filter(
        m =>
          !m.deleted_at &&
          dayjs.tz(m.start_time, timeZone).isSame(startDate, "week"),
      ) ?? [],
    [meetings, timeZone, startDate],
  );

  if (loading) {
    return (
      <Modal open closeViaMaskClick={false} closeViaEscButton={false}>
        <LoadingModal />
      </Modal>
    );
  }

  if (!!error) {
    history.replace(Routes.activityPath({ uid: activityUid }));
    return <></>;
  }

  // @ts-ignore TS(2339): Property 'enrollmentMeetingPurchaseForm' does not ... Remove this comment to see the full error message
  const { enrollmentMeetingPurchaseForm, currentUser } = data;
  const {
    canPayLater,
    activity,
    learner,
    conflictingEnrollments,
    meetingRequests,
    enrollmentsForActivityAndParent,
    pendingEnrollments,
    orgTeacher,
  } = enrollmentMeetingPurchaseForm ?? {};

  const prepayType = isOrganizationAdminWhoCanEnroll(currentUser)
    ? PrepayType.CharterAdmin
    : isPrepayMeetings === "true" && !!User.isAdmin(currentUser)
    ? PrepayType.CharterPO
    : isEsaSession
    ? PrepayType.ClassWallet
    : null;
  const isCharterPrepay =
    prepayType === PrepayType.CharterAdmin ||
    prepayType === PrepayType.CharterPO;

  const meetingEnd = meetingStart
    .add(activity.duration_minutes, "minutes")
    .toDate();

  const suggestedWeeklyMeetings = activity.suggestedWeeklyMeetings ?? 1;
  const actualWeeklyMeetings = meetingsWeekOfStartDate.length;

  if (
    !showMultipleMeetingsModal &&
    suggestedWeeklyMeetings > 1 &&
    actualWeeklyMeetings < suggestedWeeklyMeetings
  ) {
    setShowMultipleMeetingsModal();
  }

  if (
    showMultipleMeetingsModal &&
    suggestedWeeklyMeetings > 1 &&
    actualWeeklyMeetings >= suggestedWeeklyMeetings
  ) {
    clearShowMultipleMeetingsModal();
  }

  const completePaymentFlow = async ({
    enrollment,
  }: {
    enrollment?: SettleOrderMutation["settleOrder"]["enrollments"][number];
  }) => {
    const shouldShowSavePaymentForm =
      !!currentUser?.hasEsaMembership &&
      !defaultPaymentMethod &&
      !!isRecurringEnrollment;

    setEsaShowSavePayment(shouldShowSavePaymentForm);
    setSectionUid(enrollment?.section_uid || null);
    setPaymentComplete();
  };

  /**
   * This is not called for ClassWallet checkouts since the enrollment is
   * initially put into a pending state
   */
  const handlePaymentComplete = async ({
    enrollment,
    enrollmentMeetingPurchase,
    isFirstPurchaseByUser,
    orderSlugId,
    enrollmentDuration,
  }: {
    enrollment: SettleOrderMutation["settleOrder"]["enrollments"][number];
    enrollmentMeetingPurchase:
      | SettleOrderEnrollmentMeetingPurchaseMutation["settleOrderEnrollmentMeetingPurchase"]["enrollmentMeetingPurchase"]
      | CreateEnrollmentMeetingPurchaseMutation["createEnrollmentMeetingPurchase"]["enrollmentMeetingPurchase"];
    orderSlugId: string;
    isFirstPurchaseByUser: boolean;
    enrollmentDuration?: string; // needs to come in as a string for tracking
  }) => {
    // TODO: These enforcements are because of some weird behavior in
    //       EnrollmentMeetingPurchaseQuery and currentUserQuery, probably some race condition as both queries ask for
    //       current user. Once Apollo is upgraded, try to cleanup these logics.
    if (data) {
      try {
        refetch();
      } catch (err) {
        // longstanding bug with refetch blowing up, see https://sentry.io/organizations/outschool/issues/1530195572
        // explicit log to gather more info on state
        OsPlatform.captureError(err, { extra: { propsData: data } });
      }
    }
    appState.apolloClient.query({
      query: currentUserQuery,
      fetchPolicy: "network-only",
    });
    setIsRecurringEnrollment(enrollmentDuration !== "1");

    const totalCents = totalSellerPriceCentsForEnrollments([enrollment]);

    const dollarAmount = centsToDollars(totalCents);
    // @ts-ignore TS(2339): Property 'activity' does not exist on type '{ __ty... Remove this comment to see the full error message
    const { activity } = data.enrollmentMeetingPurchaseForm;

    if (totalCents) {
      trackeServerSideOrderCompleted({
        enrollmentConfirmedAt: enrollmentMeetingPurchase?.confirmedAt,
        name: activity.title,
        orderId: orderSlugId,
        price: activity.price_cents,
        productId: activity.slug_id,
        quantity: 1,
        total: totalCents,
      });

      trackOrderCompleted({
        activityListing: { activity, leader: activity.leader },
        // @ts-ignore TS(2322): Type '{ __typename?: "Enrollment" | undefined; isF... Remove this comment to see the full error message
        enrollments: [enrollment],
        section: {
          // @ts-ignore TS(2322): Type 'string | null' is not assignable to type 'st... Remove this comment to see the full error message
          uid: sectionUid,
        },
        user: currentUser,
        enrollmentDuration,
      });
    }

    if (isFirstPurchaseByUser) {
      trackAdEvent("New Buyer Acquired", {
        revenue: dollarAmount,
        total: dollarAmount,
        currency: currencyCode,
      });
      trackNewBuyerAcquiredCapi({
        amount: totalCents,
      });
    }
  };

  const handleEsaSavedPaymentClose = () => {
    setEsaShowSavePayment(false);
    setEsaSavePaymentComplete(true);
  };

  const handleClose = (closeCalendarModal?: boolean) => {
    if (esaShowSavePayment) {
      handleEsaSavedPaymentClose();
    } else if (paymentComplete) {
      if (isCharterPrepay) {
        history.replace(Routes.activityPath(activity));
      } else if (!showMultipleMeetingsModal && sectionUid) {
        history.replace(enrolledPath(sectionUid));
      } else {
        history.replace(
          // @ts-ignore TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
          Routes.websiteRoutes.enrollmentConfirmationPath(sectionUid),
        );
      }
    } else if (
      window.location.href.indexOf("enrolled") > 0 &&
      closeCalendarModal
    ) {
      const enrollURLString = "/enroll";
      if (window.location.href.indexOf(enrollURLString) > 0) {
        const newPath = window.location.pathname.slice(
          0,
          window.location.pathname.indexOf(enrollURLString) -
            enrollURLString.length,
        );
        history.replace(newPath);
      }
      // @ts-ignore
    } else if (location.state && location.state.returnLocation) {
      // @ts-ignore
      history.replace(location.state.returnLocation);
    } else if (!!enrollmentUid) {
      history.goBack();
    } else {
      // @ts-ignore TS(2339): Property 'activity' does not exist on type '{ __ty... Remove this comment to see the full error message
      const { activity } = data.enrollmentMeetingPurchaseForm;
      history.replace(Routes.activityPath(activity));
    }
  };

  const handleMultipleMeetingsModalClose = () => {
    clearShowMultipleMeetingsModal();
  };

  let currentView = null;

  if (loading && !activity) {
    // @ts-ignore TS(2322): Type 'Element' is not assignable to type 'null'.
    currentView = <LoadingModal />;
  } else if (
    paymentComplete &&
    !showMultipleMeetingsModal &&
    !isCharterPrepay &&
    !esaShowSavePayment
  ) {
    if (esaSavePaymentComplete) {
      currentView = null;
    } else if (!!enrollmentUid && sectionUid) {
      onClose();
      history.replace(enrolledPath(sectionUid));
    } else {
      history.replace(
        // @ts-ignore TS(2345): Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
        Routes.websiteRoutes.enrollmentConfirmationPath(sectionUid),
      );
    }
  } else if (esaShowSavePayment) {
    currentView = (
      <EsaMembershipSavePaymentMethod onClose={handleEsaSavedPaymentClose} />
    );
  } else {
    // @ts-ignore TS(2322): Type 'Element' is not assignable to type 'null'.
    currentView = (
      <EnrollmentMeetingPurchaseWithStripe
        canPayLater={canPayLater}
        activity={activity}
        userTimeZone={timeZone}
        payingUser={currentUser}
        enrollingParent={currentUser}
        onPaymentComplete={handlePaymentComplete}
        onCompleteFlow={completePaymentFlow}
        refetchMeetings={onClose}
        onClose={() => handleClose(true)}
        learner={learner}
        startTime={meetingStartTime}
        endTime={meetingEnd}
        meetingRequestUids={meetingRequestUids}
        conflictingEnrollments={conflictingEnrollments}
        pendingEnrollments={pendingEnrollments}
        enrollmentsInOtherSections={enrollmentsForActivityAndParent}
        onMultipleMeetingsModalClose={handleMultipleMeetingsModalClose}
        actualWeeklyMeetings={actualWeeklyMeetings}
        meetingRequests={meetingRequests}
        orgTeacher={orgTeacher}
        showMultipleMeetingsModal={showMultipleMeetingsModal}
        showCharterPurchaseComplete={isCharterPrepay}
        prepayType={prepayType}
        recurringScheduleUid={recurringScheduleUid}
        sectionUid={urlSectionUid}
      />
    );
  }

  /*
   * We only allow the parent to close the Modal via the top button
   * in "paymentComplete" mode. Otherwise, it is possible to close
   * the payment modal while a request is in flight, breaking our flows.
   */
  return (
    <Modal
      open={!esaSavePaymentComplete}
      onClose={handleClose}
      closeViaMaskClick={false}
      closeViaEscButton={false}
      hasCloseButton={paymentComplete}
      sx={{
        padding: "1.5em",
        maxWidth: "611px",
      }}
    >
      {currentView}
    </Modal>
  );
}
